import React, { useState, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Tooltip,
  Skeleton,
  Button,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
} from '@mui/icons-material';
import {
  CALENDAR_CONFIG,
  REQUEST_TYPE_COLORS,
  REQUEST_TYPE_LABELS,
  TOOLTIP_MESSAGES,
} from '../../constants';
import { TimeOffRequest } from '../../types';

interface Holiday {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  description?: string;
  isActive: boolean;
}

interface TimeOffCalendarProps {
  data: TimeOffRequest[];
  holidays: Holiday[];
  loading: boolean;
  onViewRequest: (request: TimeOffRequest) => void;
}

const TimeOffCalendar: React.FC<TimeOffCalendarProps> = ({
  data,
  holidays,
  loading,
  onViewRequest,
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());

  const calendarData = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay() + 1); // Start from Monday
    
    const endDate = new Date(lastDay);
    endDate.setDate(endDate.getDate() + (7 - lastDay.getDay())); // End on Sunday
    
    const days = [];
    const current = new Date(startDate);
    
    while (current <= endDate) {
      const dayRequests = data.filter(request => {
        const requestStart = new Date(request.startDate);
        const requestEnd = new Date(request.endDate);
        const dayDate = new Date(current);
        dayDate.setHours(0, 0, 0, 0);
        
        // Set request dates to start of day for proper comparison
        requestStart.setHours(0, 0, 0, 0);
        requestEnd.setHours(0, 0, 0, 0);
        
        return dayDate >= requestStart && dayDate <= requestEnd;
      });

      // Check if this day is a holiday
      const dayHoliday = holidays.find(holiday => {
        if (!holiday.isActive) return false;
        
        const holidayStart = new Date(holiday.startDate);
        const holidayEnd = new Date(holiday.endDate);
        const dayDate = new Date(current);
        
        // Set all dates to start of day for proper comparison
        holidayStart.setHours(0, 0, 0, 0);
        holidayEnd.setHours(23, 59, 59, 999);
        dayDate.setHours(0, 0, 0, 0);
        
        return dayDate >= holidayStart && dayDate <= holidayEnd;
      });
      
      days.push({
        date: new Date(current),
        requests: dayRequests,
        holiday: dayHoliday,
        isCurrentMonth: current.getMonth() === month,
        isToday: current.toDateString() === new Date().toDateString(),
      });
      
      current.setDate(current.getDate() + 1);
    }
    
    return days;
  }, [currentDate, data]);

  const handlePreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Skeleton variant="text" width={200} height={40} />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Skeleton variant="rectangular" width={40} height={40} />
              <Skeleton variant="rectangular" width={80} height={40} />
              <Skeleton variant="rectangular" width={40} height={40} />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {Array.from({ length: 42 }).map((_, index) => (
              <Box key={index} sx={{ width: 'calc(14.28% - 8px)', minWidth: 80 }}>
                <Skeleton variant="rectangular" height={100} />
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        {/* Calendar Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            {CALENDAR_CONFIG.MONTH_NAMES[currentDate.getMonth()]} {currentDate.getFullYear()}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton onClick={handlePreviousMonth} size="small">
              <ChevronLeftIcon />
            </IconButton>
            <Button
              variant="outlined"
              size="small"
              startIcon={<TodayIcon />}
              onClick={handleToday}
            >
              Today
            </Button>
            <IconButton onClick={handleNextMonth} size="small">
              <ChevronRightIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Day Names */}
        <Box sx={{ display: 'flex', mb: 1 }}>
          {CALENDAR_CONFIG.DAY_NAMES.map((day) => (
            <Box
              key={day}
              sx={{
                flex: 1,
                p: 1,
                textAlign: 'center',
                fontWeight: 600,
                color: 'text.secondary',
                fontSize: '0.875rem',
              }}
            >
              {day}
            </Box>
          ))}
        </Box>

        {/* Calendar Grid */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
          {calendarData.map((day, index) => (
            <Box
              key={index}
              sx={{
                flex: '1 1 calc(14.28% - 8px)',
                minWidth: 80,
                minHeight: 100,
                p: 1,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: day.isToday ? 'action.hover' : 'background.paper',
                opacity: day.isCurrentMonth ? 1 : 0.5,
                position: 'relative',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            >
              {/* Date */}
              <Typography
                variant="body2"
                sx={{
                  fontWeight: day.isToday ? 700 : 400,
                  color: day.isToday ? 'primary.main' : 'text.primary',
                  mb: 1,
                }}
              >
                {day.date.getDate()}
              </Typography>

              {/* Holiday Indicator */}
              {day.holiday && (
                <Tooltip title={day.holiday.name} arrow>
                  <Chip
                    label={day.holiday.name}
                    size="small"
                    sx={{
                      bgcolor: 'warning.main',
                      color: 'black',
                      fontSize: '0.7rem',
                      height: 20,
                      width: '100%',
                      cursor: 'pointer',
                      mb: 0.5,
                      '&:hover': {
                        opacity: 0.8,
                      },
                    }}
                  />
                </Tooltip>
              )}

              {/* Requests */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {day.requests.slice(0, CALENDAR_CONFIG.MAX_VISIBLE_REQUESTS).map((request) => (
                  <Tooltip
                    key={request.id}
                    title={`${request.employee.firstName} ${request.employee.lastName} - ${REQUEST_TYPE_LABELS[request.type]}`}
                    arrow
                  >
                    <Chip
                      label={`${request.employee.firstName} ${request.employee.lastName.substring(0, 1)}.`}
                      size="small"
                      sx={{
                        bgcolor: REQUEST_TYPE_COLORS[request.type],
                        color: 'white',
                        fontSize: '0.7rem',
                        height: 20,
                        cursor: 'pointer',
                        '&:hover': {
                          opacity: 0.8,
                        },
                      }}
                      onClick={() => onViewRequest(request)}
                    />
                  </Tooltip>
                ))}
                
                {day.requests.length > CALENDAR_CONFIG.MAX_VISIBLE_REQUESTS && (
                  <Tooltip
                    title={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                          {TOOLTIP_MESSAGES.ADDITIONAL_REQUESTS}
                        </Typography>
                        {day.requests.slice(CALENDAR_CONFIG.MAX_VISIBLE_REQUESTS).map((request) => (
                          <Typography key={request.id} variant="body2" sx={{ mb: 0.5 }}>
                            • {request.employee.firstName} {request.employee.lastName} - {REQUEST_TYPE_LABELS[request.type]}
                          </Typography>
                        ))}
                      </Box>
                    }
                    arrow
                    placement="top"
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ 
                        fontSize: '0.7rem',
                        cursor: 'pointer',
                        '&:hover': {
                          color: 'text.primary',
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      +{day.requests.length - CALENDAR_CONFIG.MAX_VISIBLE_REQUESTS} more
                    </Typography>
                  </Tooltip>
                )}
              </Box>
            </Box>
          ))}
        </Box>

        {/* Legend */}
        <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="subtitle2" sx={{ mr: 1 }}>
            Legend:
          </Typography>
          <Chip
            label={REQUEST_TYPE_LABELS.VACATION}
            size="small"
            sx={{ bgcolor: REQUEST_TYPE_COLORS.VACATION, color: 'white' }}
          />
          <Chip
            label={REQUEST_TYPE_LABELS.SICK_DAY}
            size="small"
            sx={{ bgcolor: REQUEST_TYPE_COLORS.SICK_DAY, color: 'white' }}
          />
          <Chip
            label={REQUEST_TYPE_LABELS.PERSONAL}
            size="small"
            sx={{ bgcolor: REQUEST_TYPE_COLORS.PERSONAL, color: 'white' }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default TimeOffCalendar; 